import React, { useState, useEffect } from 'react';
import { Search, Users, X, Loader2 } from 'lucide-react';
import { CollapsibleSection } from './CollapsibleSection';
import { fetchWithAuth } from '../../services/api';

interface KeycloakUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface UserLinkingSectionProps {
  selectedUser: KeycloakUser | null;
  onUserSelect: (user: KeycloakUser | null) => void;
  disabled?: boolean;
  darkMode?: boolean;
}

export const UserLinkingSection: React.FC<UserLinkingSectionProps> = ({
  selectedUser,
  onUserSelect,
  disabled = false,
  darkMode = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<KeycloakUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await fetchWithAuth(
          `/api/v1/keycloak/users/search?q=${encodeURIComponent(searchQuery)}`
        );

        if (response.ok) {
          const users = await response.json();
          setSearchResults(users || []);
          setShowResults(true);
        }
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSelectUser = (user: KeycloakUser) => {
    onUserSelect(user);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleRemoveUser = () => {
    onUserSelect(null);
  };

  return (
    <CollapsibleSection
      title="Vincular Usuário"
      icon={Users}
      badge="Opcional"
      defaultOpen={!!selectedUser}
      darkMode={darkMode}
    >
      <div className="pt-3 space-y-3">
        {selectedUser ? (
          <div className={`flex items-center justify-between p-3 rounded-lg border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${
                darkMode ? 'bg-gray-700 text-white' : 'bg-black text-white'
              }`}>
                {selectedUser.firstName?.[0] || selectedUser.username[0]}
              </div>
              <div>
                <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {selectedUser.firstName} {selectedUser.lastName || selectedUser.username}
                </p>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedUser.email}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveUser}
              disabled={disabled}
              className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${
                darkMode
                  ? 'text-gray-400 hover:text-red-400 hover:bg-red-900/20'
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={disabled}
                placeholder="Buscar usuário por nome ou email..."
                className={`w-full py-2 pl-9 pr-3 text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  darkMode
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500'
                    : 'border border-gray-300'
                } ${disabled ? (darkMode ? 'opacity-50' : 'bg-gray-100') : ''}`}
              />
              {isSearching && (
                <Loader2 className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              )}
            </div>

            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 border rounded-lg shadow-lg max-h-48 overflow-y-auto ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                {searchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelectUser(user)}
                    className={`w-full flex items-center gap-3 p-2.5 text-left transition-colors ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {user.firstName?.[0] || user.username[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {user.firstName} {user.lastName || user.username}
                      </p>
                      <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {showResults && searchResults.length === 0 && searchQuery.length >= 2 && !isSearching && (
              <div className={`absolute z-10 w-full mt-1 border rounded-lg shadow-lg p-3 text-sm text-center ${
                darkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-500'
              }`}>
                Nenhum usuário encontrado
              </div>
            )}
          </div>
        )}

        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Vincule esta pessoa a um usuário Keycloak existente para acesso ao sistema.
        </p>
      </div>
    </CollapsibleSection>
  );
};

export default UserLinkingSection;
